import React from 'react';
import {useLayoutEffect, useState, useRef, useEffect, createContext, useContext} from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './common/shared.css';
import App, { LocaleContext, LoginContext, OverlayContext, StateContext } from './common/App';
import reportWebVitals from './common/reportWebVitals';
import {Route, Routes, BrowserRouter, Link, useSearchParams, useLocation, useNavigate} from 'react-router-dom';

import { LoadState, MediationStatus } from './common/enums';
import PrivateRoute from './common/PrivateRoute';
import LoginCheck from './common/LoginCheck';
import { MailConfirmed } from './register/MailConfirmed';
import { BePatient } from './register/BePatient';
import { WebsiteApp } from './website/WebsiteApp';
import { DashboardApp } from './dashboard/DashboardApp';
import {ConfirmMail} from './register/ConfirmMail';
import {CollectData} from './register/CollectData';
import { ClosePopup, DropdownInfo, LinkSmart, LoadButton, PopupFixed, SquareButton, SwitchButton, TextInput } from './common/Common';
import { CustomerRoute } from './CustomerRoute';
import { system, cookiesVersion, maxUsernameLength } from './common/globals';
import { checkEmail, postWithCsrf, resetLoginStatesConsumer, setLoginStatesFromJsonConsumer } from './common/util';
import { LogDisplay } from './common/LogDisplay';
import { StatisticCookieInfo } from './website/StatisticCookieInfo';
import { MarketingCookieInfo } from './website/MarketingCookieInfo';
import { EssentialCookieInfo } from './website/EssentialCookieInfo';

// Create a Context  
export const ConsumerContext = createContext();

const ConsumerApp = ({}) => {
    const login = useContext(LoginContext);
    const locale = useContext(LocaleContext);
    const overlay = useContext(OverlayContext);
    const state = useContext(StateContext);

    const location = useLocation();
    const navigate = useNavigate();

    const [collectDataKey, setCollectDataKey] = useState(0); 

    const [showCookies, setShowCookies] = useState(false);
    const [showCookieInfoArr, setShowCookieInfoArr] = useState([false, false, false]);

    // 0: essential, 1: statistic, 2: marketing
    const [cookiesArr, setCookiesArr] = useState([true, true, true]);


    // For ConsumerContext
    const [mediationStatus, setMediationStatus] = useState("");
    const [declined, setDeclined] = useState("");
    const [protectionFeeSkipped, setProtectionFeeSkipped] = useState(false);
    const [mediationFeeSkipped, setMediationFeeSkipped] = useState(false);
    const [deleteAccountDisplayed, setDeleteAccountDisplayed] = useState(false);
    const [newsletter, setNewsletter] = useState(false);
    

    const [newsletterPopupDisplayed, setNewsletterPopupDisplayed] = useState(false);
    const [newsletterSubscribedPopupDisplayed, setNewsletterSubscribedPopupDisplayed] = useState("");

    const [subscribeNewsletterState, setSubscribeNewsletterState] = useState(LoadState.NONE);
    const [newsletterEmail, setNewsletterEmail] = useState("");


    // FUNCTIONS:

    function handleLoginStatusChanged(json) {
        // console.log("setting med: " + json.mediation_status);

        if(json === null) {
            resetLoginStatesConsumer(login, {setMediationStatus, setDeclined, setProtectionFeeSkipped, setMediationFeeSkipped, setNewsletter});
        }
        else {
            setLoginStatesFromJsonConsumer(json, {setMediationStatus, setDeclined, setProtectionFeeSkipped, setMediationFeeSkipped, setNewsletter});
        }
    }

    function saveCookieSettings(arr) {
        localStorage.setItem("mn_cookie_settings", JSON.stringify({
            version: cookiesVersion,
            arr: arr
        }));

        setCookiesFromArr(arr, 'update');
    }

    function mygtag(...args) {
        if(system === "production") {
            window.gtag(...args);
        }
    }

    function setCookiesFromArr(arr, mode) {
        const statistic = arr[1];
        const marketing = arr[2];

        mygtag('consent', mode, {
            'ad_storage': marketing ? 'granted' : 'denied',
            'ad_user_data': marketing ? 'granted' : 'denied',
            'ad_personalization': marketing ? 'granted' : 'denied',
            'analytics_storage': statistic ? 'granted' : 'denied'
        });

        if(!statistic) {
            deleteCookie('_ga');
        }

         if(!marketing) {
            deleteCookie('_gcl');
        }
    }

    function deleteCookie(startStr, path) {  
        document.cookie.split(';').forEach((item) => {
            if(item.trim().startsWith(startStr)) {
                const name = item.split("=")[0];
                document.cookie = name + '=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT';
            }
        });
    }

    function handleSubscribeNewsletter() {
        try {
            postWithCsrf("/user/subscribe_newsletter", JSON.stringify({email: newsletterEmail}), state).then(response => {
                if(response.ok) {
                    setSubscribeNewsletterState(LoadState.LOAD_SUCCESS);
                }
                else {
                    throw new Error("Response code: " + response.status);
                }
            }).catch(error => {
                console.log(error);
                setSubscribeNewsletterState(LoadState.LOAD_ERROR);
            });

            // Calling setConfirmLoadState() after postWithCsrf() as it could throw an exception
            setSubscribeNewsletterState(LoadState.LOADING);
        }
        catch(e) {
            console.log(e);
        }
    }


    // EFFECTS:

    useLayoutEffect(() => {
        document.title = document.title + (system === "staging" ? " (Staging)" : "");
    }, []);

    useLayoutEffect(() => {
        if(showCookies) {
            overlay.setOverlay(true);
            return () => overlay.setOverlay(false);
        }
    }, [showCookies]);

    useLayoutEffect(() => {
        if(login.loggedIn) {
            setNewsletterEmail(login.username);
        }
        else {
            setNewsletterEmail("");
        }
    }, [login.loggedIn]);

    useLayoutEffect(() => {
        if(newsletterPopupDisplayed) {
            setSubscribeNewsletterState(LoadState.NONE);
        }
    }, [newsletterPopupDisplayed]);

    useEffect(() => {
        let initCookiesVersion = null;
        const cookiesSettingsStr = localStorage.getItem("mn_cookie_settings");

        if(cookiesSettingsStr) {
            const cookiesSettings = JSON.parse(cookiesSettingsStr);

            var initCookiesArr = cookiesSettings.arr;
            initCookiesVersion = cookiesSettings.version;
        }

        if(initCookiesVersion !== cookiesVersion) {
            mygtag('consent', 'default', {
                'ad_storage': 'denied',
                'ad_user_data': 'denied',
                'ad_personalization': 'denied',
                'analytics_storage': 'denied'
            });

            setShowCookies(true);
        }
        else {
            setCookiesFromArr(initCookiesArr, 'default');
            setCookiesArr(initCookiesArr);
        }

        mygtag('js', new Date());
        mygtag('config', 'AW-16819016250');

        mygtag('event', 'conversion', {'send_to': 'AW-16819016250/5UT-CKfexZoaELqk99M-'});
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search); 

        if(params.get("newsletter_subscribed") !== null) {
            setNewsletterSubscribedPopupDisplayed("subscribed");

            params.delete("newsletter_subscribed");         
            navigate(`?${params.toString()}`, { replace: true });
        }

        if(params.get("subscribe_newsletter") !== null) {
            setNewsletterPopupDisplayed(true);

            params.delete("subscribe_newsletter");         
            navigate(`?${params.toString()}`, { replace: true });
        }
        
    }, []);


    // RENDER:

    return (
        <ConsumerContext.Provider value={{ mediationStatus, setMediationStatus, declined, setDeclined, protectionFeeSkipped, setProtectionFeeSkipped,
                                           mediationFeeSkipped, setMediationFeeSkipped, deleteAccountDisplayed, setDeleteAccountDisplayed,
                                           setShowCookies, setNewsletterPopupDisplayed, setNewsletterSubscribedPopupDisplayed, newsletter, setNewsletter }}>
            <Routes>
                <Route path="/" element={<LoginCheck onLoginStatusChanged={handleLoginStatusChanged} />}>
                    <Route path="/*" element={ <WebsiteApp />} />

                    <Route path="/" element={ <WebsiteApp />} />
                            
                    <Route exact path="/mail_confirmed" element={<MailConfirmed/>}></Route>

                    <Route path="/" element={<PrivateRoute />}>
                        <Route path="/" element={<CustomerRoute />}>
                            <Route exact path="/confirm_mail" element={<ConfirmMail/>}></Route>
                            <Route exact path="/collect_data/*" element={<CollectData key={collectDataKey}
                                    setKey={setCollectDataKey} />}></Route>
                            <Route exact path="/be_patient" element={<BePatient />}></Route>
                            
                            <Route path="/dashboard/*" element={<DashboardApp/>}/>
                        </Route>
                    </Route>
                </Route>
            </Routes>

            <div id="cookies" className={showCookies ? "" : "cookies_hidden"}>
                <div id="cookies_content">
                    <div className="head">{locale.msgs.COOKIE_SETTINGS}</div>

                    {/*<div className="cookies_descr text_2">{locale.msgs.COOKIE_SETTINGS_DESCR}</div>*/}

                    <div className="cookie_checkboxes">
                        <CookieBox headText={locale.msgs.REQUIRED_COOKIES} selected={true} setSelected={() => {}} noToggle={true}
                                showInfo={showCookieInfoArr[0]} onShowInfoChange={(v) => setShowCookieInfoArr(showCookieInfoArr.map((item, index) => index === 0 ? v : item))}
                                info={<EssentialCookieInfo />}
                        />

                        <CookieBox headText={locale.msgs.STATISTIC_COOKIES} selected={cookiesArr[1]}
                                showInfo={showCookieInfoArr[1]} onShowInfoChange={(v) => setShowCookieInfoArr(showCookieInfoArr.map((item, index) => index === 1 ? v : item))}
                                setSelected={() => {
                                    const newCookiesArr = [...cookiesArr];
                                    newCookiesArr[1] = !cookiesArr[1];
                                    setCookiesArr(newCookiesArr);
                                }}
                                info={<StatisticCookieInfo />}
                        />

                        <CookieBox headText={locale.msgs.MARKETING_COOKIES} selected={cookiesArr[2]}
                                showInfo={showCookieInfoArr[2]} onShowInfoChange={(v) => setShowCookieInfoArr(showCookieInfoArr.map((item, index) => index === 2 ? v : item))}
                                setSelected={() => {
                                    const newCookiesArr = [...cookiesArr];
                                    newCookiesArr[2] = !cookiesArr[2];
                                    setCookiesArr(newCookiesArr);
                                }}
                                info={<MarketingCookieInfo />}
                        />
                    </div>

                    <div className="cookies_btns">
                        <div className="cookie_btn">
                            <SquareButton text={locale.msgs.ACCEPT_ALL_COOKIES} clickable={true}
                                    onClick={() => {
                                        const newCookiesArr = [...cookiesArr].map((value, index) => true);
                                        saveCookieSettings(newCookiesArr);
                                        setCookiesArr(newCookiesArr);
                                        setShowCookies(false);
                                        setShowCookieInfoArr(showCookieInfoArr.map((item, idx) => false));
                                    }} />
                        </div>

                        <div className="cookie_btn">
                            <SquareButton text={locale.msgs.ACCEPT_SELECTED_COOKIES} white={true} clickable={true}
                                    onClick={() => {
                                        saveCookieSettings(cookiesArr);
                                        setShowCookies(false);
                                        setShowCookieInfoArr(showCookieInfoArr.map((item, idx) => false));
                                    }} />
                        </div>
                    </div>

                    <div className="cookies_footer">
                        <LinkSmart className="text_small_small" to={"/privacy_policy_cookies"} target="_blank">{locale.msgs.PRIVACY_POLICY_COOKIE_BANNER}</LinkSmart>
                        <LinkSmart className="text_small_small" to={"/terms_of_use"} target="_blank">{locale.msgs.TOS}</LinkSmart>
                        <LinkSmart className="text_small_small" to={"/imprint"} target="_blank">{locale.msgs.IMPRINT}</LinkSmart>
                        <LinkSmart className="text_small_small" to={"/privacy_policy"} target="_blank">{locale.msgs.PRIVACY_POLICY}</LinkSmart>
                    </div>
                </div>
            </div>

            <ClosePopup fixed={true} helpDisplayed={newsletterPopupDisplayed}
                    onClose={() => {
                        setNewsletterPopupDisplayed(false);
                    }}
                    content={
                        subscribeNewsletterState === LoadState.LOAD_SUCCESS ?
                            <div className="popup_box_content">
                                <div className="head flex_align_center">{locale.msgs.SUBSCRIBE_NEWSLETTER}</div>
                                <div className="text_2 margin_top_low_low"
                                        dangerouslySetInnerHTML={
                                            {
                                                __html: locale.msgs.SUBSCRIBE_NEWSLETTER_DESCR_SENT_MAIL
                                            }
                                        }
                                ></div>
                            </div>
                        :
                           <div className="popup_box_content">
                                <div className="head flex_align_center">{locale.msgs.SUBSCRIBE_NEWSLETTER}</div>
                                <div className="text_2 margin_top_low_low"
                                        dangerouslySetInnerHTML={
                                            {
                                                __html: locale.msgs.SUBSCRIBE_NEWSLETTER_DESCR
                                            }
                                        }
                                ></div>

                                <div className="newsletter_email_box">
                                    <TextInput text={locale.msgs.EMAIL} value={newsletterEmail}
                                            readOnly={login.loggedIn}
                                            onChange={(value) => setNewsletterEmail(value)}
                                            checker={(v) => checkEmail(v)}
                                            type="email" errorText={locale.msgs.ENTER_VALID_EMAIL_ADDRESS}
                                            maxLength={maxUsernameLength}
                                            manualErrorText={false}
                                            autocomplete="email"
                                            name="email_newsletter"
                                    />
                                </div>
                                
                                <div className="btn_right">
                                    <LoadButton text={locale.msgs.SUBSCRIBE} clickable={newsletterEmail} onClick={handleSubscribeNewsletter}
                                            saving={subscribeNewsletterState === LoadState.LOADING}
                                            notSaved={subscribeNewsletterState === LoadState.LOAD_ERROR}
                                            errText={locale.msgs.ERROR} />
                                </div>
                            </div>
                    }
            />

            <ClosePopup fixed={true} helpDisplayed={newsletterSubscribedPopupDisplayed !== ""}
                    onClose={() => {
                        setNewsletterSubscribedPopupDisplayed("");
                    }}
                    content={
                        <div className="popup_box_content">
                            <div className="head flex_align_center">{locale.msgs.SUBSCRIBE_NEWSLETTER}</div>
                            <div className="text_2 margin_top_low_low"
                                    dangerouslySetInnerHTML={
                                        {
                                            __html: newsletterSubscribedPopupDisplayed === "already subscribed" ? locale.msgs.SUBSCRIBE_NEWSLETTER_DESCR_ALREADY_SUBSCRIBED : locale.msgs.SUBSCRIBE_NEWSLETTER_DESCR_SUCCESS
                                        }
                                    }
                            ></div>
                        </div>
                    }
            />

        </ConsumerContext.Provider>
    );
}

const CookieBox = ({headText, selected, setSelected, noToggle, info, showInfo, onShowInfoChange}) => {
    const locale = useContext(LocaleContext);

    return (
        <div className="cookie_box">
            <div className="cookie_head_box">
                <div className="cookie_head_btn">
                    <SwitchButton on={selected} onClick={setSelected} grayedOut={noToggle} />
                </div>

                <div className="cookie_head_text">{headText}</div>
            </div>
            
            <div className="cookie_info">
                <DropdownInfo success={false} open={showInfo} close={!showInfo} onOpenChange={(v) => {onShowInfoChange(v)}} minimal={true} /*recalcTrigger={dropdownRecalcTrigger}*/
                        /*name={ <div style={{fontWeight: 600}}>{locale.msgs.INFORMATION}</div>} */
                        name={<div style={{textDecoration: "underline"}}>{!showInfo ? locale.msgs.SHOW_INFORMATION : locale.msgs.HIDE_INFORMATION}</div>}
                        content={info}
                />
            </div>
        </div>
    );
}


const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <BrowserRouter>
        <LogDisplay>
            <App>
                <ConsumerApp/>
            </App>
        </LogDisplay>
    </BrowserRouter>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
